import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetDiaryQuery,
  useDeleteDiaryMutation
} from '@ports/internal-services/diary/diarySlice';
import { DiaryEntry, DiarySectionProps } from '@business/interfaces/photo';
import { Button, ConfirmationModal } from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import AddDiaryModal from './AddDiaryModal';

const DiarySection: React.FC<DiarySectionProps> = ({
  assetId,
  isOpen,
  onDiaryUpdate
}) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingDiary, setEditingDiary] = useState<DiaryEntry | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch diaries
  const { data: diariesData, refetch: refetchDiaries } = useGetDiaryQuery(
    { assetId, limit: 100, offset: 0 },
    { skip: !assetId || !isOpen }
  );

  const [deleteDiary] = useDeleteDiaryMutation();

  const diaries: DiaryEntry[] = (diariesData?.data?.rows as DiaryEntry[]) || [];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside a dropdown menu
      if (target.closest('.dropdown-menu')) {
        return;
      }
      setOpenMenuId(null);
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [openMenuId]);

  if (!isOpen) return null;

  const handleAddDiary = () => {
    setEditingDiary(null);
    setIsAddModalOpen(true);
  };

  const handleEditDiary = (diary: DiaryEntry) => {
    setEditingDiary(diary);
    setIsAddModalOpen(true);
    setOpenMenuId(null);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingDiary(null);
  };

  const handleDiarySaved = () => {
    refetchDiaries();
    onDiaryUpdate?.();
    handleCloseModal();
  };

  const handleDeleteClick = (diaryId: string) => {
    setDeleteConfirmId(diaryId);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteDiary({ id: deleteConfirmId, delete: 'true' }).unwrap();
      refetchDiaries();
      onDiaryUpdate?.();
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete diary:', error);
    }
  };

  const toggleMenu = (diaryId: string) => {
    setOpenMenuId(openMenuId === diaryId ? null : diaryId);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return t('diary.today');
    } else if (diffInDays === 1) {
      return t('diary.yesterday');
    } else if (diffInDays < 7) {
      return t('diary.daysAgo', { count: diffInDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <>
      <section className="bg-[var(--media-comment-bg)] rounded-md overflow-hidden shadow-lg mt-6">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-gray-100">
            {t('diary.diaries')}
          </h2>
          <Button
            onClick={handleAddDiary}
            className="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-600 transition-colors text-sm font-medium"
            btnStyle="no-outline">
            {t('diary.addDiary')}
          </Button>
        </div>

        {/* Diary Grid - 3 per row */}
        {diaries.length === 0 ? (
          <div className="text-center py-12 text-gray-400 px-6">
            <p>{t('diary.noDiaries')}</p>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {diaries.map(diary => (
              <div
                key={diary.id}
                className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 relative hover:bg-gray-800/70 transition-colors group h-[200px] flex flex-col">
                <div className="flex items-start justify-between mb-2 flex-shrink-0 relative z-10">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-100 truncate">
                      {diary.title}
                    </h3>
                    {diary.isPrivate ? (
                      <SVGAssets.LockClosedIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <SVGAssets.LockOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  <span className="text-gray-500 text-xs">
                    {formatDate(diary.createdAt)}
                  </span>
                  {/* Three Dots Menu */}
                  <div className="relative flex-shrink-0">
                    <Button
                      onClick={() => toggleMenu(diary.id)}
                      className="text-gray-400 hover:text-gray-200 p-1 rounded hover:bg-gray-700/50 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="More options"
                      btnStyle="no-outline">
                      <SVGAssets.ThreeDot className="w-5 h-5" />
                    </Button>

                    {/* Dropdown Menu */}
                    {openMenuId === diary.id && (
                      <div className="dropdown-menu absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                        <Button
                          onClick={e => {
                            e.stopPropagation();
                            handleEditDiary(diary);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-t-md"
                          btnStyle="no-outline">
                          <SVGAssets.PencilIcon className="w-4 h-4" />
                          {t('diary.edit')}
                        </Button>
                        <Button
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteClick(diary.id);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2 rounded-b-md"
                          btnStyle="no-outline">
                          <SVGAssets.Delete className="w-4 h-4 text-red-600" />
                          {t('diary.delete')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description with hidden scroll */}
                <div className="flex-1 overflow-y-auto mb-2 diary-description-scroll">
                  <p className="text-gray-400 text-sm break-words whitespace-pre-wrap pr-1">
                    {diary.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Add/Edit Diary Modal */}
      <AddDiaryModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onSave={handleDiarySaved}
        assetId={assetId}
        editingDiary={editingDiary}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        variant="delete"
        title={t('diary.deleteDiary')}
        message={t('diary.deleteDiaryMessage')}
        confirmText={t('diary.yesDelete')}
        cancelText={t('cancel')}
      />
    </>
  );
};

export default DiarySection;
